import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IonContent,
  IonIcon,
  IonPage,
  isPlatform,
  RefresherEventDetail,
  useIonViewWillEnter,
  useIonViewWillLeave,
} from '@ionic/react';
import { ImpactStyle } from '@capacitor/haptics';
import {
  Container,
  CustomButton,
  EmptySpecialistListFeature,
  PushNotificationsModal,
  Refresher,
  Specialist,
  useDeepCompareEffect,
} from '@mindly/ui-components';
import { useIntl } from 'react-intl';

// modals
// components
import WhiteScreenForAnimation from '../../components/WhiteScreenForAnimation/WhiteScreenForAnimation';

// utils
import {
  isPaginationDisabledSelector,
  specialistsBackupListSelector,
  specialistsBackupLoadingSelector,
  specialistsFiltersLoadingSelector,
  specialistsSelector,
  specilistLoadingSelector,
  specilistTotalSelector,
} from '../../redux/specialists/selector';
import { setIsAnimationActive } from '../../redux/animation/reducer';
import { getIsAnimation } from '../../redux/animation/selector';
import { userPassedOrSkipOnBoardingSelector, userSelector } from '../../redux/user/selector';

// styles
import { FavouritesHeader, PsychologistListContainer } from './PsychologistListPage.styled';
import { setActiveTab, setIsActive, Tabs } from '../../redux/tabBar/reducer';
import { useHistory } from 'react-router';
import { PrivateRoutes, PublicRoutes } from '../../routes/navigation';
import useLangs from '../../context/LangsContext';
import { globalVariables } from '../../redux/firestoreGlobalVariables/selector';
import PageHeader from '../../components/PageHeader/PageHeader';
import { heartOutline } from 'ionicons/icons';
import useToast from '../../context/ToastContext/ToastContext';
import {
  fetchSpecialistsBackupList,
  fetchSpecialistsFilters,
  fetchSpecialistsList,
} from '../../redux/specialists/thunk';
import { loadUserThunk } from '../../redux/user/thunk';
import {
  favouriteSpecialistsListSelector,
  favouriteSpecialistsLoadingSelector,
} from '../../redux/favouriteSpecialits/selector';
import { fetchFavouriteSpecialists } from '../../redux/favouriteSpecialits/thunk';
import { useTranslation } from 'react-i18next';
import { usePushNotifications } from '../../shared/context/PushNotificationsContext';
import NeedOnboardingFeature from '../../features/NeedOnboardingFeature/NeedOnboardingFeature';
import { useAnalytics } from 'src/shared';
import PsychologistListItems from './PsychologistListItems';
import { setPaddingBottomInPX, setSupportButtonActive } from 'src/redux/supportButton/reducer';
import { getUserOnBoardingFlowKeySelector } from 'src/redux/onBoardingFlow/selector';
import { useFilters } from 'src/shared/hooks/useFilters';
import { isomorficHapticsImpact } from 'src/shared/utils/haptics';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { SpecialistListControlsFeature } from 'src/features/SpecialistListControlsFeature';
import classNames from 'classnames';
import { resetPagination } from 'src/redux/specialists/reducer';

const pushNotificationPermissionImage = {
  uk: '/assets/push-notifications/uk.webp',
  pl: '/assets/push-notifications/pl.webp',
  es: '/assets/push-notifications/sp.webp',
};

/**
 * Page with all active psychologists
 */
const PsychologistListPage: React.FC = () => {
  const dispatch = useDispatch();
  const { showToast } = useToast();
  const { i18n } = useTranslation();

  const history = useHistory();
  const intl = useIntl();

  const currentUser = useSelector(userSelector);
  const onboardingKey = useSelector(getUserOnBoardingFlowKeySelector);

  const specialistsList = useSelector(specialistsSelector);
  const specialistsLoading = useSelector(specilistLoadingSelector);
  const isPaginationDisabled = useSelector(isPaginationDisabledSelector);
  const favouriteSpecialistList = useSelector(favouriteSpecialistsListSelector);
  const favouriteSpecialistListStatus = useSelector(favouriteSpecialistsLoadingSelector);
  const [isWhiteScreen, setIsWhiteScreen] = useState(true);
  const isAnimationActive = useSelector(getIsAnimation);
  const globalVars = useSelector(globalVariables);
  const pageRef = useRef();
  const { locale } = useLangs();
  const {
    t,
    i18n: { language },
  } = useTranslation(['app', 'specialists', 'ui']);
  const { t: uiT } = useTranslation('ui');
  const { trackPredefinedEvent } = useAnalytics();
  const [isFetchingPsychologists, setIsFetchingPsychologists] = useState<boolean>(false);
  const [isNewSpecialistsLoad, setIsNewSpecialistsLoad] = useState<boolean>(false);
  const pushNotificationsState = usePushNotifications();
  const loadingFiltersState = useSelector(specialistsFiltersLoadingSelector);
  const backupSpecialistsList = useSelector(specialistsBackupListSelector);
  const specialistsTotal = useSelector(specilistTotalSelector);
  const isBackupSpecialistsListLoading = useSelector(specialistsBackupLoadingSelector);
  const { filtersDataForAnalytics, filtersVersion } = useFilters();
  const isAllowUpdateTrackSpecialistListViewed = useRef(true);
  const userPassedOrSkipOnBoarding = useSelector(userPassedOrSkipOnBoardingSelector);
  const newList = useFeatureIsOn('specialist-list-v3');
  const isLocalizedAreasOfWorkByClientProblem = useFeatureIsOn('localized_areas_of_work_by_client_problem');

  const trackSpecialistListViewed = useCallback(
    (cb?: () => void) => {
      if (
        !['idle', 'pending'].includes(loadingFiltersState) &&
        specialistsLoading === 'succeeded' &&
        typeof specialistsList?.length === 'number'
      ) {
        cb?.();
        trackPredefinedEvent('SPECIALIST_LIST_VIEWED', {
          specialist_list_algorithm_version: filtersVersion,
          specialists_count: specialistsList?.length,
          specialists_total: specialistsTotal,
          ...filtersDataForAnalytics,
        });
      }
    },
    [loadingFiltersState, filtersDataForAnalytics, specialistsLoading, specialistsList?.length, specialistsTotal]
  );

  useDeepCompareEffect(() => {
    if (!isAllowUpdateTrackSpecialistListViewed.current) return;
    trackSpecialistListViewed(() => {
      isAllowUpdateTrackSpecialistListViewed.current = false;
    });
    return () => {
      isAllowUpdateTrackSpecialistListViewed.current = true;
    };
  }, [trackSpecialistListViewed]);

  useIonViewWillEnter(() => {
    if (isAllowUpdateTrackSpecialistListViewed.current) return;
    trackSpecialistListViewed();
  }, [trackSpecialistListViewed]);

  const showMoreButtonHandler = useCallback(async () => {
    try {
      setIsNewSpecialistsLoad(true);
      await dispatch(fetchSpecialistsList(locale));
    } catch (error: any) {
      showToast({ error });
    } finally {
      setIsNewSpecialistsLoad(false);
    }
  }, [currentUser?.specialist_order?.length, locale]);

  useIonViewWillEnter(() => {
    if (!currentUser?.id) {
      history.push(PublicRoutes.AUTH);
      return;
    }
    dispatch(setIsActive(true));
    dispatch(setActiveTab(Tabs.first));
  }, []);

  // Disabling white screen for smoother animation
  useEffect(() => {
    setTimeout(() => {
      setIsWhiteScreen(false);
    }, 50);
    setTimeout(() => {
      dispatch(setIsAnimationActive(false));
    }, 1050);
  }, []);

  useIonViewWillEnter(async () => {
    if (!currentUser?.id) return;
    if (
      !currentUser?.is_viewed_specialist_list &&
      (!currentUser?.onBoardingAnswers || !Object.keys(currentUser?.onBoardingAnswers).length)
    ) {
      trackPredefinedEvent('SPECIALIST_LIST_VIEWED', {
        specialist_list_algorithm_version: filtersVersion,
        specialists_count: 0,
        specialists_total: 0,
      });
    } else {
      try {
        if (favouriteSpecialistListStatus !== 'succeeded') {
          setIsFetchingPsychologists(true);
          await dispatch(fetchFavouriteSpecialists());
        }
      } catch (e) {
      } finally {
        setIsFetchingPsychologists(false);
      }
    }

    try {
      if (specialistsLoading === 'succeeded') return;

      setIsFetchingPsychologists(true);

      // if filters are enabled and filters are not loaded, load them
      if (loadingFiltersState === 'idle') {
        try {
          await dispatch(fetchSpecialistsFilters({ t, isLocalizedAreasOfWorkByClientProblem })).unwrap();
        } catch (error) {
          console.log(error);
        }
      }

      const specialistsResponse = await dispatch(fetchSpecialistsList(locale)).unwrap();
      pushNotificationsState.setIsOpenPushNotificationModal(pushNotificationsState.pushNotificationStatus === 'prompt');

      if (!specialistsResponse.list?.length) {
        await dispatch(fetchSpecialistsBackupList());
      }

      await dispatch(loadUserThunk(currentUser?.id)).unwrap();
    } catch (error: any) {
      showToast({ error });
    } finally {
      setIsFetchingPsychologists(false);
    }
  }, [
    currentUser?.id,
    currentUser?.onBoardingAnswers,
    currentUser?.specialist_order,
    specialistsLoading,
    favouriteSpecialistListStatus,
    locale,
    globalVars,
    i18n.language,
    loadingFiltersState,
    filtersVersion,
    trackPredefinedEvent,
    t,
  ]);

  useIonViewWillEnter(() => {
    dispatch(setSupportButtonActive(language === 'es'));
    dispatch(setPaddingBottomInPX(67));
  }, [language]);

  useIonViewWillLeave(() => {
    dispatch(setSupportButtonActive(false));
  });

  const handleOnboardingRestart = () => {
    history.push(PublicRoutes.ON_BOARDING_FLOW.replace(':flowId', onboardingKey));
  };

  const handleFavouriteButtonClick = useCallback(async () => {
    isomorficHapticsImpact({ style: ImpactStyle.Light });
    history.push(PrivateRoutes.FAVOURITE_SPECIALISTS_TAB);
  }, []);

  const showFavouriteBadge = useMemo(() => !!favouriteSpecialistList?.length, [favouriteSpecialistList]);

  const handleChangeFiltersClick = useCallback(() => {
    history.push(PrivateRoutes.PSYCHOLOGIST_LIST_FILTERS);
  }, []);

  const isLoading = useMemo(
    () => isFetchingPsychologists || specialistsLoading === 'force-pending',
    [isFetchingPsychologists, specialistsLoading]
  );

  const refreshSpecialistsList = useCallback(
    async (e: CustomEvent<RefresherEventDetail>) => {
      try {
        dispatch(resetPagination());
        await dispatch(fetchSpecialistsList(locale));
      } catch (error) {
        showToast({ error });
      } finally {
        e.detail.complete();
      }
    },
    [locale]
  );

  if (typeof userPassedOrSkipOnBoarding === 'boolean' && !userPassedOrSkipOnBoarding) {
    return (
      <IonPage ref={pageRef}>
        <PageHeader
          showLogo={!newList}
          showChatButton={!newList}
          showAvatar={false}
          showFilters={!newList}
          toolbarClassName={classNames({ '![--min-height:0px]': newList })}
        >
          {!newList ? (
            <FavouritesHeader
              isDesktop={!isPlatform('hybrid')}
              onClick={handleFavouriteButtonClick}
              className="relative !p-2"
            >
              <IonIcon icon={heartOutline} />
              {showFavouriteBadge && <span className={'badge'} />}
            </FavouritesHeader>
          ) : null}
        </PageHeader>

        <IonContent>
          <Container>
            <div className={'flex flex-1 justify-center items-center'}>
              <NeedOnboardingFeature
                title={t('specialists:therapist_list.empty.title')}
                subtitle={t('specialists:therapist_list.empty.description')}
                buttonLabel={t('specialists:therapist_list.empty.start_button')}
                onClick={onboardingKey ? handleOnboardingRestart : undefined}
              />
            </div>
          </Container>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage ref={pageRef}>
      <PageHeader
        showLogo={!newList}
        showFilters={!newList}
        showAvatar={false}
        showChatButton={!newList}
        showPromoCodeTimer
        toolbarClassName={classNames({ '![--min-height:0px] [&_div]:gap-0': newList })}
      >
        {!newList ? (
          <FavouritesHeader
            isDesktop={!isPlatform('hybrid')}
            onClick={handleFavouriteButtonClick}
            className="relative !p-2"
          >
            <IonIcon icon={heartOutline} />
            {showFavouriteBadge && <span className={'badge'} />}
          </FavouritesHeader>
        ) : null}
      </PageHeader>

      {isAnimationActive && <WhiteScreenForAnimation isShow={isWhiteScreen} />}

      <IonContent>
        <Refresher onIonRefresh={refreshSpecialistsList} disabled={!specialistsList?.length} />
        <Container style={{ padding: 0 }}>
          {newList && <SpecialistListControlsFeature isLoading={isFetchingPsychologists} />}
          <PsychologistListContainer className="flex-1">
            <PsychologistListItems isLoading={isLoading} specialistsList={specialistsList as unknown as Specialist[]} />
            {!isPaginationDisabled && !!specialistsList?.length ? (
              <CustomButton
                buttonType={'border'}
                loading={isNewSpecialistsLoad}
                style={{
                  margin: '32px 0px',
                  borderRadius: '40px',
                  width: 'calc(100% - 40px)',
                  alignSelf: 'center',
                }}
                onClick={showMoreButtonHandler}
              >
                {intl.formatMessage({
                  id: 'other.showMoreSpecialists',
                  defaultMessage: 'Показати ще',
                })}
              </CustomButton>
            ) : !isLoading && !!specialistsList?.length ? (
              <>
                <div className="mb-10 px-5">
                  <div className="border-b border-borders-divider" />
                </div>
                <EmptySpecialistListFeature type="end" t={uiT} onChangeFiltersClick={handleChangeFiltersClick} />
              </>
            ) : isPaginationDisabled && !isLoading ? (
              <div className={classNames('flex flex-col flex-1', { 'mt-4': newList })}>
                <EmptySpecialistListFeature type="empty" t={uiT} onChangeFiltersClick={handleChangeFiltersClick} />
                <div className="mt-3 mb-1 px-5">
                  <div className="border-b border-borders-divider" />
                </div>
                <PsychologistListItems
                  isLoading={isBackupSpecialistsListLoading}
                  specialistsList={backupSpecialistsList as unknown as Specialist[]}
                />
              </div>
            ) : null}
          </PsychologistListContainer>
        </Container>
      </IonContent>
      <PushNotificationsModal
        canDismiss={pushNotificationsState.canDismiss}
        image={pushNotificationPermissionImage[locale as 'uk' | 'pl']}
        presentingElement={pageRef.current}
        isOpen={pushNotificationsState.isOpenPushNotificationModal}
        onApplyPermission={pushNotificationsState.allowPushNotificationHandler}
        title={t('app:allow.notifications.first.title')}
        description={t('app:allow.notifications.first.description')}
        applyPermissionText={t('app:allow.notifications.first.button')}
      />
    </IonPage>
  );
};

export default PsychologistListPage;
